function safeArray(value) {
  return Array.isArray(value) ? value.filter(Boolean) : [];
}

function cleanText(value, fallback = "") {
  const text = String(value ?? "").trim();
  return text || fallback;
}

function getPublicProfile(org) {
  return org?.publicProfile && typeof org.publicProfile === "object"
    ? org.publicProfile
    : org?.profile && typeof org.profile === "object"
      ? org.profile
      : {};
}

function isObject(value) {
  return value && typeof value === "object" && !Array.isArray(value);
}

function dateValue(value) {
  if (!value) return null;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") {
    const date = value.toDate();
    return Number.isNaN(date?.getTime?.()) ? null : date.getTime();
  }
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.getTime();
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
  }
  return null;
}

function explicitOrder(item) {
  const candidates = [
    item?.order,
    item?.index,
    item?.sortOrder,
    item?.position,
    item?.displayOrder,
  ];
  const value = candidates.find((candidate) => Number.isFinite(Number(candidate)));
  return value == null ? null : Number(value);
}

function isHiddenGalleryItem(item) {
  if (!isObject(item)) return false;
  const status = cleanText(item.status, "").toLowerCase();
  return (
    item.isActive === false ||
    item.active === false ||
    item.enabled === false ||
    item.hidden === true ||
    item.deleted === true ||
    item.archived === true ||
    status === "archived" ||
    status === "deleted"
  );
}

function normalizeGalleryItem(item, sourceField, sourceIndex) {
  if (!item) return null;

  const url =
    typeof item === "string"
      ? item
      : item.imageUrl ||
        item.url ||
        item.src ||
        item.photoUrl ||
        item.downloadUrl ||
        item.thumbnailUrl ||
        item.image ||
        "";

  if (!url || isHiddenGalleryItem(item)) return null;

  const uploadedAt =
    typeof item === "string"
      ? null
      : item.uploadedAt || item.createdAt || item.updatedAt || item.date || null;
  const order = typeof item === "string" ? null : explicitOrder(item);

  return {
    id:
      typeof item === "string"
        ? `${sourceField}-${sourceIndex}`
        : item.id || item.assetId || item.storagePath || `${sourceField}-${sourceIndex}`,
    url,
    src: url,
    imageUrl: url,
    alt:
      typeof item === "string"
        ? "Gallery image"
        : cleanText(item.alt || item.caption || item.title, "Gallery image"),
    caption:
      typeof item === "string"
        ? ""
        : cleanText(item.caption || item.title || item.description || item.body, ""),
    order: order == null ? sourceIndex : order,
    featured: typeof item === "string" ? false : item.featured === true || item.isFeatured === true,
    hasExplicitOrder: order != null,
    isActive: typeof item === "string" ? true : item.isActive !== false && item.active !== false,
    uploadedAt,
    createdAt: typeof item === "string" ? null : item.createdAt || null,
    sourceField,
    sourceIndex,
    _uploadedAtMs: dateValue(uploadedAt),
  };
}

function addSource(sources, sourceField, value) {
  if (safeArray(value).length) {
    sources.push({ sourceField, items: safeArray(value) });
  }
}

function collectGallerySources(org) {
  const profile = getPublicProfile(org);
  const sources = [];

  addSource(sources, "publicProfile.gallery", profile.gallery);
  addSource(sources, "publicProfile.galleryItems", profile.galleryItems);
  addSource(sources, "publicProfile.galleryImages", profile.galleryImages);
  addSource(sources, "publicProfile.communityGallery", profile.communityGallery);
  addSource(sources, "publicProfile.mediaGallery", profile.mediaGallery);
  addSource(sources, "publicProfile.images", profile.images);
  addSource(sources, "publicProfile.publicGallery", profile.publicGallery);
  addSource(sources, "publicProfile.website.gallery", profile.website?.gallery);
  addSource(sources, "publicProfile.websiteContent.gallery", profile.websiteContent?.gallery);
  addSource(sources, "publicProfile.homepageSections.gallery.items", profile.homepageSections?.gallery?.items);
  addSource(sources, "publicProfile.homepageSections.gallery.images", profile.homepageSections?.gallery?.images);
  addSource(sources, "publicProfile.destinationPages.gallery.items", profile.destinationPages?.gallery?.items);
  addSource(sources, "publicProfile.destinationPages.gallery.images", profile.destinationPages?.gallery?.images);
  addSource(sources, "publicProfile.sections.gallery.items", profile.sections?.gallery?.items);
  addSource(sources, "publicProfile.sections.gallery.images", profile.sections?.gallery?.images);
  addSource(sources, "publicProfile.siteSections.gallery.items", profile.siteSections?.gallery?.items);
  addSource(sources, "publicProfile.siteSections.gallery.images", profile.siteSections?.gallery?.images);

  addSource(sources, "org.gallery", org?.gallery);
  addSource(sources, "org.galleryItems", org?.galleryItems);
  addSource(sources, "org.galleryImages", org?.galleryImages);
  addSource(sources, "org.mediaGallery", org?.mediaGallery);
  addSource(sources, "org.images", org?.images);
  addSource(sources, "org.publicGallery", org?.publicGallery);
  addSource(sources, "org.website.gallery", org?.website?.gallery);
  addSource(sources, "org.websiteContent.gallery", org?.websiteContent?.gallery);
  addSource(sources, "org.homepageSections.gallery.items", org?.homepageSections?.gallery?.items);
  addSource(sources, "org.homepageSections.gallery.images", org?.homepageSections?.gallery?.images);
  addSource(sources, "org.destinationPages.gallery.items", org?.destinationPages?.gallery?.items);
  addSource(sources, "org.destinationPages.gallery.images", org?.destinationPages?.gallery?.images);
  addSource(sources, "org.sections.gallery.items", org?.sections?.gallery?.items);
  addSource(sources, "org.sections.gallery.images", org?.sections?.gallery?.images);
  addSource(sources, "org.siteSections.gallery.items", org?.siteSections?.gallery?.items);
  addSource(sources, "org.siteSections.gallery.images", org?.siteSections?.gallery?.images);

  return sources;
}

export function getPublicOrgGalleryImages(org) {
  const sources = collectGallerySources(org);
  const normalized = [];

  sources.forEach(({ sourceField, items }) => {
    items.forEach((item, index) => {
      const image = normalizeGalleryItem(item, sourceField, index);
      if (image) normalized.push(image);
    });
  });

  const seen = new Set();
  const deduped = normalized.filter((image) => {
    const key = image.url || image.id;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const sorted = deduped.sort((a, b) => {
    if (a.hasExplicitOrder || b.hasExplicitOrder) {
      const orderDiff = Number(a.order) - Number(b.order);
      if (Number.isFinite(orderDiff) && orderDiff !== 0) return orderDiff;
    }
    if (a._uploadedAtMs != null && b._uploadedAtMs != null && a._uploadedAtMs !== b._uploadedAtMs) {
      return a._uploadedAtMs - b._uploadedAtMs;
    }
    if (a.sourceIndex !== b.sourceIndex) return a.sourceIndex - b.sourceIndex;
    return String(a.sourceField).localeCompare(String(b.sourceField));
  });

  if (process.env.NODE_ENV === "development") {
    console.debug("[Lightly Gallery Source]", {
      adminSourceCount: sources.reduce((max, source) => Math.max(max, source.items.length), 0),
      compatibleCandidateCount: sources.reduce((sum, source) => sum + source.items.length, 0),
      activePublicCount: sorted.length,
      sourceFieldsUsed: sources.map((source) => source.sourceField),
    });
  }

  return sorted.map(({ _uploadedAtMs, hasExplicitOrder, ...image }) => image);
}
